import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Navigate } from "react-router-dom";

// Short grace window after a fresh page load to let onAuthStateChange deliver
// a session that's already in storage / coming back from an OAuth redirect,
// before we bounce to /login. Without this, OAuth callbacks can briefly land
// on a protected route with `user === null` and get redirected away.
const OAUTH_GRACE_MS = 1500;

export default function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const [graceElapsed, setGraceElapsed] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setGraceElapsed(true), OAUTH_GRACE_MS);
    return () => clearTimeout(t);
  }, []);

  const showSpinner = loading || (!user && !graceElapsed);

  if (showSpinner) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  // Enforce email verification before reaching any protected route. OAuth
  // providers (Google, etc.) set email_confirmed_at automatically, so they
  // pass through. Email/password signups must click the link first.
  if (!user.email_confirmed_at && !user.confirmed_at) {
    return <Navigate to="/check-email" replace state={{ email: user.email }} />;
  }

  return <>{children}</>;
}
